import { BaseEffect } from '../BaseEffect.js';

/**
 * Matrix digital rain effect implementation based on And390's Inside the Matrix shader
 * Original: https://www.shadertoy.com/view/4t3BWl
 * Uses runes code by FabriceNeyret2 and otaviogood
 */
export class MatrixEffect extends BaseEffect {
    constructor(shaderManager) {
        const defaultParams = {
            fallSpeed: 1.0,
            zoomSpeed: 0.5,
            rayAngle: 6.0,
            opacity: 0.15,
            intensity: 1.0,
            animate: true
        };

        super('matrix', shaderManager, defaultParams);
    }

    /**
     * Initialize the effect
     */
    async init() {
        const vertexSource = `
            attribute vec2 a_position;
            attribute vec2 a_texCoord;
            varying vec2 v_texcoord;
            
            void main() {
                gl_Position = vec4(a_position, 0, 1);
                v_texcoord = a_texCoord;
            }
        `;

        const fragmentSource = `
            #ifdef GL_FRAGMENT_PRECISION_HIGH
            precision highp float;
            #else
            precision mediump float;
            #endif
            
            uniform sampler2D u_image;
            uniform float u_time;
            uniform vec2 u_resolution;
            uniform float u_fallSpeed;
            uniform float u_zoomSpeed;
            uniform float u_rayAngle;
            uniform float u_opacity;
            uniform float u_intensity;
            
            varying vec2 v_texcoord;

            const int ITERATIONS = 40;
            const float STRIP_CHARS_MIN = 7.0;
            const float STRIP_CHARS_MAX = 40.0;
            const float STRIP_CHAR_WIDTH = 0.10;

            float getCharHeight() {
                float pixelDensity = max(u_resolution.x / 1000.0, 1.0);
                return 0.15 / pixelDensity;
            }

            float getXYCellSize() {
                float pixelDensity = max(u_resolution.x / 1000.0, 1.0);
                return 12.0 * STRIP_CHAR_WIDTH / sqrt(pixelDensity);
            }

            const int BLOCK_SIZE = 10;
            const int BLOCK_GAP = 2;

            const float BLOCKS_BEFORE_TURN = 3.0;
            const float PI = 3.14159265359;

            float hash(float v) {
                return fract(sin(v) * 43758.5453);
            }

            float hash(vec2 v) {
                return hash(dot(v, vec2(5.3983, 5.4427)));
            }

            vec2 hash2(vec2 v) {
                vec2 s = vec2(dot(v, vec2(127.1, 311.7)), dot(v, vec2(269.5, 183.3)));
                return fract(sin(s) * 43758.5453);
            }

            vec4 hash4(vec2 v) {
                vec4 s = vec4(
                    dot(v, vec2(127.1, 311.7)),
                    dot(v, vec2(269.5, 183.3)),
                    dot(v, vec2(113.5, 271.9)),
                    dot(v, vec2(246.1, 124.6))
                );
                return fract(sin(s) * 43758.5453);
            }

            float rune_line(vec2 p, vec2 a, vec2 b) {
                vec2 pa = p - a;
                vec2 ba = b - a;
                float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
                return length(pa - ba * h);
            }

            float rune(vec2 U, vec2 seed, float highlight) {
                float d = 1e5;
                vec4 pos;
                
                pos = hash4(seed);
                pos = (floor(pos * vec4(2.0, 3.0, 2.0, 3.0)) + 0.5) / vec4(2.0, 3.0, 2.0, 3.0);
                pos.y = 0.0;
                if (pos.xy != pos.zw) {
                    d = min(d, rune_line(U, pos.xy, pos.zw + 0.001));
                }
                
                pos = hash4(seed + vec2(1.0, 0.0));
                pos = (floor(pos * vec4(2.0, 3.0, 2.0, 3.0)) + 0.5) / vec4(2.0, 3.0, 2.0, 3.0);
                pos.x = 0.999;
                if (pos.xy != pos.zw) {
                    d = min(d, rune_line(U, pos.xy, pos.zw + 0.001));
                }
                
                pos = hash4(seed + vec2(2.0, 0.0));
                pos = (floor(pos * vec4(2.0, 3.0, 2.0, 3.0)) + 0.5) / vec4(2.0, 3.0, 2.0, 3.0);
                pos.x = 0.0;
                if (pos.xy != pos.zw) {
                    d = min(d, rune_line(U, pos.xy, pos.zw + 0.001));
                }
                
                pos = hash4(seed + vec2(3.0, 0.0));
                pos = (floor(pos * vec4(2.0, 3.0, 2.0, 3.0)) + 0.5) / vec4(2.0, 3.0, 2.0, 3.0);
                pos.y = 0.999;
                if (pos.xy != pos.zw) {
                    d = min(d, rune_line(U, pos.xy, pos.zw + 0.001));
                }

                return smoothstep(0.1, 0.0, d) + highlight * smoothstep(0.4, 0.0, d);
            }

            float random_char(vec2 outer, vec2 inner, float highlight) {
                vec2 seed = vec2(
                    fract(dot(outer, vec2(269.5, 183.3)) * 0.1),
                    fract(dot(outer, vec2(113.5, 271.9)) * 0.1)
                );
                return rune(inner, seed, highlight);
            }

            vec2 rotate(vec2 v, float a) {
                float s = sin(a);
                float c = cos(a);
                mat2 m = mat2(c, -s, s, c);
                return m * v;
            }

            vec3 rotateX(vec3 v, float a) {
                float s = sin(a);
                float c = cos(a);
                return mat3(1.0,0.0,0.0, 0.0,c,-s, 0.0,s,c) * v;
            }

            vec3 rotateY(vec3 v, float a) {
                float s = sin(a);
                float c = cos(a);
                return mat3(c,0.0,-s, 0.0,1.0,0.0, s,0.0,c) * v;
            }

            vec3 rotateZ(vec3 v, float a) {
                float s = sin(a);
                float c = cos(a);
                return mat3(c,-s,0.0, s,c,0.0, 0.0,0.0,1.0) * v;
            }

            vec3 rain(vec3 ro3, vec3 rd3, float time) {
                float STRIP_CHAR_HEIGHT = getCharHeight();
                float ZCELL_SIZE = 1.0 * (STRIP_CHAR_HEIGHT * STRIP_CHARS_MAX);
                float XYCELL_SIZE = getXYCellSize();
                
                time *= u_fallSpeed;
                vec4 result = vec4(0.0);
                vec2 ro2 = vec2(ro3);
                vec2 rd2 = normalize(vec2(rd3));

                bool prefer_dx = abs(rd2.x) > abs(rd2.y);
                float t3_to_t2 = prefer_dx ? rd3.x / rd2.x : rd3.y / rd2.y;

                ivec3 cell_side = ivec3(step(0.0, rd3));
                ivec3 cell_shift = ivec3(sign(rd3));

                float t2 = 0.0;
                ivec2 next_cell = ivec2(floor(ro2/XYCELL_SIZE));

                for (int i = 0; i < ITERATIONS; i++) {
                    ivec2 cell = next_cell;
                    float t2s = t2;

                    vec2 side = vec2(next_cell + cell_side.xy) * XYCELL_SIZE;
                    vec2 t2_side = (side - ro2) / rd2;
                    if (t2_side.x < t2_side.y) {
                        t2 = t2_side.x;
                        next_cell.x += cell_shift.x;
                    } else {
                        t2 = t2_side.y;
                        next_cell.y += cell_shift.y;
                    }

                    vec2 cell_in_block = fract(vec2(cell) / float(BLOCK_SIZE));
                    float gap = float(BLOCK_GAP) / float(BLOCK_SIZE);
                    if (cell_in_block.x < gap || cell_in_block.y < gap) continue;

                    float t3s = t2s / t3_to_t2;
                    float pos_z = ro3.z + rd3.z * t3s;
                    float xycell_hash = hash(vec2(cell));
                    float z_shift = xycell_hash * 11.0 + time * (0.5 + xycell_hash * 1.0 + pow(xycell_hash, 16.0) * 3.0);
                    float char_z_shift = floor(z_shift / STRIP_CHAR_HEIGHT);
                    z_shift = char_z_shift * STRIP_CHAR_HEIGHT;
                    int zcell = int(floor((pos_z - z_shift)/ZCELL_SIZE));

                    for (int j = 0; j < 2; j++) {
                        vec4 cell_hash = hash4(vec2(cell));
                        vec4 cell_hash2 = fract(cell_hash * vec4(127.1, 311.7, 271.9, 124.6));

                        float chars_count = cell_hash.w * (STRIP_CHARS_MAX - STRIP_CHARS_MIN) + STRIP_CHARS_MIN;
                        float target_length = chars_count * STRIP_CHAR_HEIGHT;
                        float target_rad = STRIP_CHAR_WIDTH / 2.0;
                        float target_z = float(zcell) * ZCELL_SIZE + z_shift + cell_hash.z * (ZCELL_SIZE - target_length);
                        vec2 target = vec2(cell) * XYCELL_SIZE + target_rad + cell_hash.xy * (XYCELL_SIZE - target_rad * 2.0);

                        vec2 s = target - ro2;
                        float tmin = dot(s, rd2);
                        if (tmin >= t2s && tmin <= t2) {
                            float u = s.x * rd2.y - s.y * rd2.x;
                            if (abs(u) < target_rad) {
                                u = (u/target_rad + 1.0) / 2.0;
                                float z = ro3.z + rd3.z * tmin/t3_to_t2;
                                float v = (z - target_z) / target_length;
                                if (v >= 0.0 && v < 1.0) {
                                    float c = floor(v * chars_count);
                                    float q = fract(v * chars_count);
                                    vec2 char_hash = hash2(vec2(c + char_z_shift, cell_hash2.x));
                                    
                                    if (char_hash.x >= 0.1 || c == 0.0) {
                                        float timeScale = c == 0.0 ? 5.0 : (1.0 + cell_hash2.z * 3.0);
                                        float animTime = floor(time * timeScale * 0.5) * 2.0;
                                        
                                        vec2 charSeed = vec2(
                                            char_hash.x,
                                            fract(animTime * (0.5 + cell_hash2.w * 0.5))
                                        );
                                        
                                        float a = random_char(charSeed, vec2(u,q), 
                                            max(1.0, 3.0 - (chars_count - c)/2.0) * 0.2);
                                        
                                        // Reverse fade direction - fade at top (start), bright at bottom (end)
                                        float trail = c / chars_count;
                                        trail = pow(trail, 1.5);
                                        a *= clamp(trail * 2.0, 0.0, 1.0);
                                        
                                        if (a > 0.0) {
                                            float depth = tmin/t3_to_t2;
                                            float attenuation = 1.0 + pow(0.06 * depth, 2.0);
                                            
                                            vec3 charColor;
                                            if (c >= chars_count - 1.0) {
                                                // Bright white for characters at the bottom
                                                charColor = vec3(1.0) * u_intensity * 2.0;
                                            } else {
                                                float trailGlow = pow(trail, 0.5);
                                                vec3 greenGlow = vec3(0.25, 0.80, 0.40) * u_intensity;
                                                charColor = greenGlow * (1.0 + trailGlow);
                                            }
                                            
                                            charColor /= attenuation;
                                            
                                            float a1 = result.a;
                                            float newAlpha = a1 + (1.0 - a1) * a;
                                            result.xyz = (result.xyz * a1 + charColor * (1.0 - a1) * a) / max(newAlpha, 0.001);
                                            result.a = newAlpha;
                                            
                                            if (result.a > 0.98) return result.xyz;
                                        }
                                    }
                                }
                            }
                        }
                        zcell += cell_shift.z;
                    }
                }

                return result.xyz * result.a;
            }

            void main() {
                vec2 uv = (v_texcoord * 2.0 - 1.0) * u_resolution.y/u_resolution.y;
                float time = u_time;

                float z_offset = time * u_zoomSpeed * -2.0;
                vec3 ro = vec3(0.0, -z_offset, 5.0 + z_offset);
                vec3 rd = normalize(vec3(uv.x, 1.0, uv.y));
                rd = rotateX(rd, u_rayAngle);
                
                vec3 col = rain(ro, rd, time);
                
                vec3 baseColor = texture2D(u_image, v_texcoord).rgb;
                col *= (1.0 + u_intensity);
                vec3 finalColor = mix(baseColor, col, u_opacity);
                gl_FragColor = vec4(finalColor, 1.0);
            }
        `;

        await super.init(vertexSource, fragmentSource);
    }

    /**
     * Set up effect-specific uniforms
     */
    setupUniforms() {
        super.setupUniforms();
        this.addUniform('u_fallSpeed', { type: '1f' });
        this.addUniform('u_zoomSpeed', { type: '1f' });
        this.addUniform('u_rayAngle', { type: '1f' });
        this.addUniform('u_opacity', { type: '1f' });
        this.addUniform('u_intensity', { type: '1f' });
        this.addUniform('u_resolution', { type: '2f' });
    }

    /**
     * Update effect-specific uniforms
     * @param {Object} uniforms - Uniform values to update
     */
    updateUniforms(uniforms) {
        Object.assign(uniforms, {
            u_fallSpeed: { value: this.params.fallSpeed, type: '1f' },
            u_zoomSpeed: { value: this.params.zoomSpeed, type: '1f' },
            u_rayAngle: { value: this.params.rayAngle * Math.PI / 180.0, type: '1f' },
            u_opacity: { value: this.params.opacity, type: '1f' },
            u_intensity: { value: this.params.intensity, type: '1f' }
        });

        if (!this.params.animate) {
            uniforms.u_time.value = 0;
        }
    }

    /**
     * Get UI configuration for parameters
     * @returns {Object} UI configuration
     */
    getUIConfig() {
        return {
            fallSpeed: {
                min: 0.1,
                max: 5.0,
                step: 0.1,
                label: 'Fall Speed'
            },
            zoomSpeed: {
                min: 0.0,
                max: 3.0,
                step: 0.1,
                label: 'Zoom Speed'
            },
            rayAngle: {
                min: -45.0,
                max: 45.0,
                step: 1.0,
                label: 'Ray Angle (degrees)'
            },
            opacity: {
                min: 0.0,
                max: 1.0,
                step: 0.05,
                label: 'Opacity'
            },
            intensity: {
                min: 0.5,
                max: 3.0,
                step: 0.1,
                label: 'Brightness'
            },
            animate: {
                label: 'Animate'
            }
        };
    }
} 